<template>
  <v-container>
    <v-row>
      <v-col>
        <v-text-field
          :label="$t('surveyView.searchTerm')"
          v-model="query"
          append-icon="mdi-magnify"
          outlined
          clearable
        >
        </v-text-field>
      </v-col>
      <v-col cols="1" v-if="isAdministrator">
        <v-btn
          class="mt-1"
          tile
          large
          color="primary"
          icon
          @click="showSurveyEditForm({ startDate: null, endDate: null })"
        >
          <v-icon>mdi-comment-plus-outline</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-n8">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="surveys"
          :search="query"
          :loading="loading"
          :footer-props="dataTableFooterProps"
          :options="{
            sortBy: ['date'],
            sortDesc: [true],
          }"
        >
          <template #[`footer.page-text`]="{ pageStart,pageStop,itemsLength }">
            {{ $t("pageText", { pageStart, pageStop, itemsLength }) }}
          </template>
          <template v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="item in items"
                :key="item._id"
                :class="`${item.voted ? 'grey' : 'black'}--text`"
              >
                <td>
                  {{ item.title }}
                </td>
                <td>{{ item.startDate | formatDate }}</td>
                <td>{{ item.endDate | formatDate }}</td>
                <td v-if="isAdministrator">
                  <span
                    v-for="userGroup in item.userGroups.split(',')"
                    :key="userGroup"
                  >
                    <v-chip
                      color="primary"
                      text-color="white"
                      class="mt-1 mb-1 mr-2"
                    >
                      {{ $t(`groups.${userGroup}`) }}
                    </v-chip>
                  </span>
                </td>
                <td v-if="isAdministrator">
                  <v-btn
                    tile
                    large
                    color="primary"
                    icon
                    @click="showSurveyEditForm(item)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    tile
                    large
                    color="primary"
                    icon
                    @click="showResultForm(item)"
                    v-if="hasResults(item)"
                  >
                    <v-icon>mdi-chart-gantt</v-icon>
                  </v-btn>
                  <v-btn
                    tile
                    large
                    color="primary"
                    icon
                    @click="showDeleteConfirmationForm(item)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
                <td v-else>
                  <v-btn
                    v-if="!item.showReport"
                    tile
                    large
                    :color="item.voted ? 'grey' : 'primary'"
                    icon
                    @click="showVoteForm(item)"
                  >
                    <v-icon>mdi-vote-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    tile
                    large
                    color="primary"
                    icon
                    @click="showResultForm(item)"
                  >
                    <v-icon>mdi-chart-gantt</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="editSurveyDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t(`surveyView.${editSurvey._id ? "edit" : "add"}Survey`) }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeSurveyEditForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form :lazy-validation="true">
            <validation-observer ref="validationObserver" v-slot="{ invalid }">
              <v-row class="mt-2 mb-2">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.title')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      v-model="editSurvey.title"
                      :label="$t('form.fields.title') + ' *'"
                      required
                      outlined
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row class="mt-n8 mb-2">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.question')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-textarea
                      outlined
                      v-model="editSurvey.question"
                      required
                      :label="$t('form.fields.question') + ' *'"
                      :error-messages="errors"
                    ></v-textarea>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row class="mt-n8 mb-2">
                <v-col>
                  <v-combobox
                    v-model="userGroups"
                    :items="allGroups"
                    :label="$t('form.fields.groups') + ' *'"
                    multiple
                    outlined
                  >
                    <template v-slot:item="{ item, attrs }">
                      <v-checkbox
                        v-model="attrs.inputValue"
                        :label="$t(`groups.${item}`)"
                        color="primary"
                      ></v-checkbox>
                    </template>
                    <template v-slot:selection="{ attrs, item, selected }">
                      <v-chip
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        @click:close="removeGroup(item)"
                      >
                        {{ $t(`groups.${item}`) }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-menu
                    ref="showDatePickerFrom"
                    v-model="showDatePickerFrom"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <validation-provider
                        :name="$t('form.fields.startDate')"
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          :value="formattedTimeValueFrom"
                          :label="$t('form.fields.startDate') + ' *'"
                          prepend-icon="mdi-calendar"
                          required
                          outlined
                          :error-messages="errors"
                          readonly
                          v-on="on"
                        >
                        </v-text-field
                      ></validation-provider>
                    </template>
                    <v-date-picker
                      v-model="editSurvey.startDate"
                      no-title
                      @input="hideDatePicker"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col>
                  <v-menu
                    ref="showDatePickerTo"
                    v-model="showDatePickerTo"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <validation-provider
                        :name="$t('form.fields.endDate')"
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          :value="formattedTimeValueTo"
                          :label="$t('form.fields.endDate') + ' *'"
                          prepend-icon="mdi-calendar"
                          required
                          outlined
                          :error-messages="errors"
                          readonly
                          v-on="on"
                        >
                        </v-text-field
                      ></validation-provider>
                    </template>
                    <v-date-picker
                      v-model="editSurvey.endDate"
                      no-title
                      @input="hideDatePicker"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-text-field
                    v-model="newAnswer"
                    :label="$t('form.fields.answer')"
                    persistent-hint
                    outlined
                  ></v-text-field>
                </v-col>
                <v-btn
                  class="mt-4 mr-3"
                  tile
                  large
                  color="primary"
                  icon
                  @click="addAnswer"
                  :disabled="!newAnswer"
                >
                  <v-icon>mdi-plus-box</v-icon>
                </v-btn>
              </v-row>
              <v-row class="mt-n12 mb-2">
                <v-col>
                  <v-switch
                    v-model="editSurvey.multipleChoice"
                    :label="$t('form.fields.multipleChoice')"
                  ></v-switch>
                </v-col>
                <v-col>
                  <v-switch
                    v-model="editSurvey.anonymize"
                    :label="$t('form.fields.anonymize')"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row class="mt-n8 mb-2">
                <v-col>
                  <v-card v-if="editSurvey.answers.length > 0">
                    <v-list class="mb-n2 mt-n2" dense>
                      <v-list-item
                        v-for="(answer, index) in editSurvey.answers"
                        :key="answer.title"
                      >
                        <v-list-item-content
                          v-html="answer.title"
                        ></v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            class="mt-n2"
                            tile
                            large
                            icon
                            color="primary"
                            @click="deleteAnswer(index)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider
                        v-if="index < editSurvey.answers.length - 1"
                      ></v-divider>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  :disabled="invalid || !canSaveSurvey()"
                  @click="saveSurvey()"
                >
                  {{ $t("form.btn.save") }}
                </v-btn>
              </v-card-actions>
            </validation-observer>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
      </v-card>
    </v-dialog>
    <v-dialog v-model="voteSurveyDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("surveyView.vote") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="voteSurveyDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <div class="title">{{ editSurvey.title }}</div>
              <div class="subtitle-1 mt-2">{{ editSurvey.question }}</div>
              <v-item-group
                v-model="answers"
                multiple
                v-if="editSurvey.multipleChoice"
              >
                <div
                  v-for="(answer, index) in editSurvey.answers"
                  v-bind:key="answer.id"
                >
                  <div v-if="editSurvey.voted">
                    <v-checkbox
                      input-value="true"
                      :label="answer.title"
                      hide-details
                      disabled
                      v-if="answers.indexOf(index) >= 0"
                    />
                    <v-checkbox
                      :label="answer.title"
                      hide-details
                      disabled
                      v-else
                    />
                  </div>
                  <div v-else>
                    <v-item #default="{ active, toggle }">
                      <v-checkbox
                        @change="toggle(!active)"
                        :label="answer.title"
                        hide-details
                      />
                    </v-item>
                  </div>
                </div>
              </v-item-group>
              <v-radio-group v-model="answers" v-else>
                <div
                  v-for="answer in editSurvey.answers"
                  v-bind:key="answer.id"
                  class="mt-2 mb-2"
                >
                  <v-radio :label="answer.title" :disabled="editSurvey.voted" />
                </div>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-card-actions v-if="!editSurvey.voted">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="answers.length === 0"
              @click="saveVote()"
            >
              {{ $t("surveyView.vote") }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resultDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("surveyView.result") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="resultDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <div class="title">{{ editSurvey.title }}</div>
              <div class="subtitle-1 mt-2">{{ editSurvey.question }}</div>
            </v-col>
          </v-row>
          <v-row v-if="isAdministrator" class="mt-n8">
            <v-col>
              <v-switch
                v-model="editSurvey.showReport"
                :label="$t('form.fields.showReport')"
                :hint="
                  editSurvey.showReport
                    ? $t('surveyView.showReportDoneHint')
                    : $t('surveyView.showReportHint')
                "
                persistent-hint
                @change="approveReport"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row v-if="editSurvey.amountUsers">
            <v-col>
              <v-progress-linear
                :value="(100 * totalAnswers) / editSurvey.amountUsers"
                color="primary"
                height="25"
              >
                <template v-slot="{ value }">
                  <span class="white--text font-weight-bold">
                    {{ $t("surveyView.turnout", { value: Math.ceil(value) }) }}
                    ({{ totalAnswers }} / {{ editSurvey.amountUsers }})
                  </span>
                </template>
              </v-progress-linear>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <bar-chart
                max-width="600px"
                :labels="chartLabels"
                :data="chartData"
                @onBarClick="showVoters"
              ></bar-chart>
            </v-col>
          </v-row>
          <v-row class="mt-n2" v-if="!editSurvey.anonymize && isAdministrator">
            <v-col>
              <div class="subtitle-2 font-weight-bold">
                {{
                  currentAnswer
                    ? currentAnswer.title
                    : $t("surveyView.noAnswerSelected")
                }}
              </div>
              <span v-for="voter in voters" :key="voter">
                <v-chip
                  color="primary"
                  text-color="white"
                  class="mt-1 mb-1 mr-2"
                >
                  {{ voter }}
                </v-chip>
              </span>
            </v-col>
          </v-row>
          <v-row v-if="isAdministrator">
            <v-col>
              <v-autocomplete
                v-model="removeUserVoteId"
                :items="resultUsersValues"
                :label="$t('view.users')"
                :disabled="resultUsersValues.length === 0"
                outlined
              ></v-autocomplete>
            </v-col>
            <v-btn
              class="mt-5 mr-3"
              color="primary"
              :disabled="!removeUserVoteId || resultUsersValues.length === 0"
              @click="resetSurveyAnswer"
            >
              <v-icon left>mdi-comment-remove-outline</v-icon>
              {{ $t("surveyView.resetAnswers") }}
            </v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteSurveyDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("surveyView.deleteSurvey") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="
            $t('surveyView.deleteSurveyText', {
              title: editSurvey.title,
            })
          "
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteSurvey()">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="deleteSurveyDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { i18n } from "../plugins/i18n";
import {
  surveyResource,
  surveyVoteResource,
  surveyResetResource,
  usersGroupsResource,
  surveyMailResource,
} from "../api/resources";
import { SelectionUserGroups } from "../config/constants";
import * as moment from "moment";
import BarChart from "./BarChart";

export default {
  components: { BarChart },
  data: () => ({
    query: null,
    loading: false,
    editSurveyDialog: false,
    deleteSurveyDialog: false,
    voteSurveyDialog: false,
    resultDialog: false,
    currentAnswer: null,
    voters: null,
    resultUsers: [],
    showDatePickerFrom: false,
    showDatePickerTo: false,
    removeUserVoteId: null,
    newAnswer: "",
    answers: [],
    userGroups: [],
    allGroups: SelectionUserGroups.filter(
      (element) => element !== "administrator"
    ),
    editSurvey: { answers: [] },
    oldSurvey: null,
    surveys: [],
  }),
  mounted() {
    moment.locale(i18n.locale);
    this.loadSurveys();
  },
  watch: {
    currentGroups() {
      this.loadSurveys();
    },
  },
  computed: {
    ...mapGetters([
      "isAdministrator",
      "currentGroups",
      "currentUser",
      "currentGroups",
      "dataTableFooterProps",
    ]),
    headers() {
      const headers = [
        { text: this.$t("form.fields.title"), value: "title" },
        { text: this.$t("form.fields.startDate"), value: "startDate" },
        { text: this.$t("form.fields.endDate"), value: "endDate" },
      ];
      if (this.isAdministrator) {
        headers.push({
          text: this.$t("form.fields.groups"),
          value: "userGroups",
        });
      }
      headers.push({ text: "", value: "actions", sortable: false });
      return headers;
    },
    formattedTimeValueFrom() {
      return moment(this.editSurvey.startDate).format("LL");
    },
    formattedTimeValueTo() {
      return moment(this.editSurvey.endDate).format("LL");
    },
    chartLabels() {
      if (!this.editSurvey.answers) {
        return [];
      }
      return this.editSurvey.answers.map((answer) => answer.title);
    },
    chartData() {
      if (!this.editSurvey.answers) {
        return [];
      }
      return this.editSurvey.answers.map((answer) =>
        answer.userIds ? answer.userIds.length : 0
      );
    },
    totalAnswers() {
      if (!this.editSurvey.answers) {
        return 0;
      }
      const userIds = [];
      this.editSurvey.answers.forEach((answer) =>
        userIds.push(...answer.userIds.map((element) => element.id))
      );
      return Array.from(new Set(userIds)).length;
    },
    resultUsersValues() {
      return this.resultUsers.map((user) => ({
        value: user.id,
        text: user.username,
      }));
    },
    locale() {
      return i18n.locale;
    },
  },
  methods: {
    showSurveyEditForm(survey) {
      this.editSurvey = survey;
      this.newAnswer = "";
      this.oldSurvey = JSON.stringify(survey);
      if (!this.editSurvey.startDate) {
        this.editSurvey.startDate = moment().format("YYYY-MM-DD");
      }
      if (!this.editSurvey.endDate) {
        this.editSurvey.endDate = moment().format("YYYY-MM-DD");
      }
      if (!this.editSurvey.answers) {
        this.editSurvey.answers = [];
      }
      this.userGroups = this.editSurvey.userGroups
        ? this.editSurvey.userGroups.split(",")
        : [];
      this.editSurveyDialog = true;
    },
    addAnswer() {
      this.editSurvey.answers.push({ title: this.newAnswer });
      this.newAnswer = "";
    },
    hasResults(survey) {
      for (const answer of survey.answers) {
        if (answer.userIds.length > 0) {
          return true;
        }
      }
      return false;
    },
    closeSurveyEditForm() {
      if (this.editSurvey._id) {
        const oldSurvey = JSON.parse(this.oldSurvey);
        this.$set(
          this.surveys,
          this.surveys.findIndex((survey) => survey._id === oldSurvey._id),
          oldSurvey
        );
      }
      this.editSurveyDialog = false;
      this.$refs.validationObserver.reset();
    },
    showDeleteConfirmationForm(survey) {
      this.editSurvey = survey;
      this.deleteSurveyDialog = true;
    },
    showVoteForm(survey) {
      this.editSurvey = survey;
      this.answers = [];
      this.editSurvey.answers.forEach((answer, index) => {
        if (
          answer.userIds
            .map((element) => element.id)
            .indexOf(this.currentUser.id) >= 0
        ) {
          this.answers.push(index);
        }
      });
      this.editSurvey.voted = this.answers.length > 0;
      if (!this.editSurvey.multipleChoice && this.editSurvey.voted) {
        this.answers = this.answers[0];
      }
      // survey expired
      if (
        !moment().isBetween(
          this.editSurvey.startDate,
          moment(this.editSurvey.endDate).add(1, "d"),
          "[]"
        )
      ) {
        this.editSurvey.voted = true;
      }
      this.voteSurveyDialog = true;
    },
    async showResultForm(survey) {
      this.editSurvey = survey;
      this.voters = null;
      this.currentAnswer = null;
      this.resultUsers = [];
      if (this.isAdministrator || !survey.anonymize) {
        const userGroups = this.editSurvey.userGroups.split(",");
        for (const userGroup of userGroups) {
          const users = (
            await usersGroupsResource.get(userGroup, {
              headers: {
                "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
              },
            })
          ).data;
          this.resultUsers.push(...users);
        }
      }
      this.resultDialog = true;
    },
    async deleteSurvey() {
      await surveyResource.delete(this.editSurvey._id, {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      this.surveys.splice(this.surveys.indexOf(this.editSurvey), 1);
      this.deleteSurveyDialog = false;
    },
    showVoters(index) {
      if (this.editSurvey.anonymize) {
        return;
      }
      this.currentAnswer = this.editSurvey.answers[index];
      this.voters = this.currentAnswer.userIds.map(
        (entry) =>
          this.resultUsers.find((user) => user.id === entry.id).username
      );
    },
    hideDatePicker() {
      this.showDatePickerFrom = false;
      this.showDatePickerTo = false;
    },
    removeGroup(item) {
      this.userGroups.splice(this.userGroups.indexOf(item), 1);
    },
    deleteAnswer(index) {
      const oldAnswer = this.newAnswer;
      this.editSurvey.answers.splice(index, 1);
      // TODO why do i have to do this to force array reload?
      this.newAnswer += " ";
      this.newAnswer = oldAnswer;
    },
    async loadSurveys() {
      this.loading = true;
      this.surveys = [];
      if (this.isAdministrator) {
        this.surveys = (
          await surveyResource.get("", {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data.results;
      } else {
        this.currentGroups.forEach(async (group) => {
          const groupSurveys = (
            await surveyResource.get("", {
              params: { userGroups: group },
              headers: {
                "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
              },
            })
          ).data.results;
          groupSurveys.forEach((survey) => {
            if (
              this.surveys.findIndex((entry) => entry._id === survey._id) < 0
            ) {
              for (const answer of survey.answers) {
                if (!answer.userIds) {
                  answer.userIds = [];
                }
                if (
                  answer.userIds
                    .map((element) => element.id)
                    .indexOf(this.currentUser.id) >= 0
                ) {
                  survey.voted = true;
                  break;
                }
              }
              if (
                !moment().isBetween(
                  survey.startDate,
                  moment(survey.endDate).add(1, "d"),
                  "[]"
                )
              ) {
                survey.voted = true;
              }
              this.surveys.push(survey);
            }
          });
        });
      }
      this.loading = false;
    },
    async saveSurvey() {
      this.editSurvey.userGroups = this.userGroups.join(",");

      // create new survey
      if (!this.editSurvey._id) {
        await surveyResource.post("", this.editSurvey, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        });
        if (this.editSurvey.startDate === moment().format("YYYY-MM-DD")) {
          surveyMailResource.post(
            "",
            {
              mailData: { survey: this.editSurvey },
              template: "userSurveyNew",
              subject: `Neue Umfrage ${this.editSurvey.title}`,
            },
            {
              headers: {
                "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
              },
            }
          );
        }
        await this.loadSurveys();
      } else {
        await surveyResource.put(this.editSurvey._id, this.editSurvey, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        });
      }

      this.editSurveyDialog = false;
      this.$refs.validationObserver.reset();
    },
    async approveReport() {
      await surveyResource.put(
        this.editSurvey._id,
        { showReport: this.editSurvey.showReport },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );

      if (this.editSurvey.showReport) {
        surveyMailResource.post(
          "",
          {
            mailData: { survey: this.editSurvey },
            template: "userSurveyResult",
            subject: `Auswertung für die Umfrage ${this.editSurvey.title} verfügbar`,
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
      }
    },
    async saveVote() {
      await surveyVoteResource.post(
        "",
        {
          id: this.editSurvey._id,
          answers: Array.isArray(this.answers) ? this.answers : [this.answers],
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.voteSurveyDialog = false;
      await this.loadSurveys();
    },
    async resetSurveyAnswer() {
      await surveyResetResource.post(
        "",
        {
          id: this.editSurvey._id,
          userId: this.removeUserVoteId,
          answerCount: this.editSurvey.answers.length,
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.removeUserVoteId = null;
    },
    canSaveSurvey() {
      if (this.userGroups.length === 0 || this.editSurvey.answers.length <= 1) {
        return false;
      }
      if (this.editSurvey.startDate > this.editSurvey.endDate) {
        return false;
      }
      if (this.editSurvey._id) {
        if (this.userGroups.join(",") !== this.editSurvey.userGroups) {
          return true;
        }
        return JSON.stringify(this.editSurvey) !== this.oldSurvey;
      }
      return true;
    },
  },
};
</script>
